import { formatShortDateInZone } from '@sportninja/common/ts-utils/date-utils'

export type WaiverStatus =
  | 'signed'
  | 'signed_outdated'
  | 'not_signed'
  | 'revoked'
export type WaiverEvidenceLevel =
  | 'full'
  | 'attested'
  | 'imported'
  | 'derived'
  | 'legacy'

export type WaiverTone = 'success' | 'info' | 'warning' | 'error' | 'neutral'

export type WaiverIconKind =
  | 'signed'
  | 'guardian'
  | 'paper'
  | 'legacy'
  | 'outdated'
  | 'not_signed_required'
  | 'not_signed'
  | 'revoked'

export interface WaiverStatusInput {
  status?: WaiverStatus | null
  evidenceLevel?: WaiverEvidenceLevel | null
  signedAt?: string | null
  signedByName?: string | null
  signerRelationship?: string | null
  /** Where an imported signature came from, e.g. `docusign:<envelope id>`. */
  externalReference?: string | null
  revokedAt?: string | null
  revokedReason?: string | null
  isRequiredToPlay?: boolean
  isSignedFallback?: boolean
}

export interface WaiverStatusAppearance {
  tone: WaiverTone
  icon: WaiverIconKind
  label: string
  tooltip: string
}

const LEGACY_SIGNED: WaiverStatusAppearance = {
  tone: 'neutral',
  icon: 'legacy',
  label: 'Signed (legacy)',
  tooltip:
    'Marked signed before detailed records were kept. No signing details were captured.',
}

const clean = (value?: string | null): string => value?.trim() || ''

const notSigned = (isRequiredToPlay?: boolean): WaiverStatusAppearance =>
  isRequiredToPlay
    ? {
        tone: 'error',
        icon: 'not_signed_required',
        label: 'Not signed',
        tooltip:
          "Hasn't signed. This competition requires a signed waiver before a player can play.",
      }
    : {
        tone: 'neutral',
        icon: 'not_signed',
        label: 'Not signed',
        tooltip: "Hasn't signed the waiver yet.",
      }

const signed = (input: WaiverStatusInput): WaiverStatusAppearance => {
  const name = clean(input.signedByName)
  const date = formatShortDateInZone(input.signedAt)
  const onDate = date ? ` on ${date}` : ''

  switch (input.evidenceLevel) {
    // Brought over from another system. With a signing date it is a real, dated
    // signature; without one it is no better than a legacy tick.
    case 'imported': {
      if (!date) {
        return LEGACY_SIGNED
      }
      const source = clean(input.externalReference)
        .toLowerCase()
        .startsWith('docusign:')
        ? 'DocuSign'
        : 'another system'
      return {
        tone: 'success',
        icon: 'signed',
        label: 'Signed',
        tooltip: `Signed${onDate}. Imported from ${source}.`,
      }
    }
    case 'legacy':
      return LEGACY_SIGNED
    // Dated from the registration or the invitation they accepted, which is when
    // they agreed — but nothing else about the signing was captured.
    case 'derived':
      return {
        tone: 'success',
        icon: 'signed',
        label: 'Signed',
        tooltip: date
          ? `Signed${onDate}. Recorded before signing details were kept, so there is no signature record.`
          : 'Signed. Recorded before signing details were kept.',
      }
    case 'attested':
      return {
        tone: 'info',
        icon: 'paper',
        label: 'Signed on paper',
        tooltip:
          name || date
            ? `Recorded${name ? ` by ${name}` : ''}${onDate}.`
            : 'Signed on paper.',
      }
    default: {
      const relationship = clean(input.signerRelationship)
      if (relationship && relationship !== 'self') {
        return {
          tone: 'success',
          icon: 'guardian',
          label: 'Signed by guardian',
          tooltip: `Signed by ${
            name ? `${name} (parent/guardian)` : 'a parent/guardian'
          }${onDate}.`,
        }
      }
      return {
        tone: 'success',
        icon: 'signed',
        label: 'Signed',
        tooltip: `Signed${name ? ` by ${name}` : ''}${onDate}.`,
      }
    }
  }
}

/**
 * Decides how a roster player's waiver state reads: colour, icon, label and
 * tooltip copy. Returns null when there is nothing to show (no status and no
 * legacy boolean). Red is reserved for "not signed" on a competition that
 * requires a signature to play.
 */
export const resolveWaiverStatus = (
  input: WaiverStatusInput
): WaiverStatusAppearance | null => {
  switch (input.status) {
    case 'signed':
      return signed(input)
    case 'signed_outdated': {
      const date = formatShortDateInZone(input.signedAt)
      return {
        tone: 'warning',
        icon: 'outdated',
        label: 'Waiver updated',
        tooltip: date
          ? `Signed on ${date}. The waiver text has changed since then.`
          : 'The waiver text has changed since this player signed.',
      }
    }
    case 'not_signed':
      return notSigned(input.isRequiredToPlay)
    case 'revoked': {
      const date = formatShortDateInZone(input.revokedAt)
      const reason = clean(input.revokedReason).replace(/[.\s]+$/, '')
      return {
        tone: 'warning',
        icon: 'revoked',
        label: 'Revoked',
        tooltip: `Signature revoked${date ? ` on ${date}` : ''}${
          reason ? `: ${reason}` : ''
        }.`,
      }
    }
    default:
      break
  }

  if (input.isSignedFallback === true) {
    return LEGACY_SIGNED
  }
  if (input.isSignedFallback === false) {
    return notSigned(input.isRequiredToPlay)
  }
  return null
}
